import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, ExternalLink, Loader2, Mail, RefreshCw, ShieldCheck, Trash2, Wrench } from "lucide-react";
import { useGwsPanel } from "./gws/useGwsPanel";
import { Guard } from "./gws/GwsBits";
import { GwsConnectForm } from "./gws/GwsConnectForm";
import { GwsMatrixTable } from "./gws/GwsMatrixTable";
import { GwsDkimBox } from "./gws/GwsDkimBox";

/**
 * Per-site Google Workspace panel — adds the domain to the Workspace
 * (alias / secondary), writes the DNS matrix (verification TXT, MX, SPF,
 * DKIM, DMARC, mail CNAME) into Cloudflare and tracks verification.
 * Credentials are network-wide (Settings → Google Workspace).
 */
export default function GoogleWorkspacePanel({ site, reload }) {
  const p = useGwsPanel(site, reload);

  if (!site.domain) return <Guard testid="gws-no-domain">Purchase or attach a domain on the Domain tab before connecting Google Workspace.</Guard>;
  if (!site.cloudflare_zone_id) return <Guard testid="gws-no-cf-zone">Google Workspace integration writes MX, SPF, DKIM and verification records into Cloudflare — set up the Cloudflare zone first.</Guard>;

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5 space-y-4" data-testid="gws-panel">
      <div className="flex items-center gap-3">
        <Mail size={18} className="text-sky-400" />
        <div className="font-display text-lg font-bold">Google Workspace</div>
        {p.connected && (
          <span data-testid="gws-state-badge" className={`ml-auto inline-flex items-center gap-1 text-[11px] mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${p.verified ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>
            {p.verified ? <CheckCircle2 size={11} /> : <Clock size={11} />} {p.verified ? "verified" : "pending verification"} · {p.status?.mode || site.gws_mode}
          </span>
        )}
      </div>

      <ConfigNotices cfg={p.cfg} />
      {!p.connected && <GwsConnectForm site={site} cfg={p.cfg} form={p.form} setField={p.setField} busyKind={p.busyKind} onConnect={p.actions.connect} />}
      {p.connected && <ConnectedView site={site} p={p} />}
      {p.msg && <div className="text-xs text-emerald-400 mono" data-testid="gws-msg">{p.msg}</div>}
      {p.err && <div className="text-xs text-red-400 mono" data-testid="gws-err">{p.err}</div>}
    </div>
  );
}

function ConnectedView({ site, p }) {
  return (
    <>
      <Toolbar p={p} />
      <GwsMatrixTable matrix={p.matrix} busy={p.busy} />
      {!p.dkimConfigured && <GwsDkimBox site={site} status={p.status} value={p.form.dkim} onChange={(v) => p.setField("dkim", v)} onSave={p.actions.saveDkim} busyKind={p.busyKind} />}
      {p.status?.last_error && !p.verified && (
        <div data-testid="gws-last-error" className="text-[11px] text-amber-400/90 mono break-words">last Google response: {p.status.last_error}</div>
      )}
      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-zinc-800">
        <a href="https://admin.google.com/ac/domains/manage" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"><ExternalLink size={12} /> Manage domains in Admin console</a>
        <button data-testid="gws-disconnect-btn" onClick={p.actions.disconnect} disabled={p.busy} className="inline-flex items-center gap-1 text-zinc-500 hover:text-red-400 px-2 py-1.5 rounded-sm text-xs font-medium ml-auto disabled:opacity-50">
          <Trash2 size={12} /> Disconnect + delete DNS
        </button>
      </div>
    </>
  );
}

function ConfigNotices({ cfg }) {
  if (cfg && !cfg.configured) {
    return (
      <div data-testid="gws-not-configured" className="border border-amber-500/30 bg-amber-500/5 text-amber-300 rounded-sm p-3 text-xs leading-relaxed">
        Google Workspace isn&apos;t configured for the network yet. Add the service account + super-admin once in{" "}
        <Link to="/admin/settings" className="underline hover:text-amber-100">Platform Settings → Google Workspace</Link>; every site then connects with one click.
      </div>
    );
  }
  if (cfg?.configured && cfg.probe && !cfg.probe.ok) {
    return (
      <div data-testid="gws-probe-error" className="border border-rose-500/30 bg-rose-500/5 text-rose-300 rounded-sm p-3 text-xs leading-relaxed">
        Workspace credentials are failing: {cfg.probe.error}
      </div>
    );
  }
  return null;
}

function Toolbar({ p }) {
  const { actions: a } = p;
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="label-tiny">DNS records ({p.matrix.length})</div>
      <div className="flex items-center gap-2">
        <select data-testid="gws-inbound-live" value={p.currentInbound} disabled={p.busy} onChange={(e) => a.changeInbound(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs outline-none disabled:opacity-50" title="Who receives mail for this domain">
          <option value="workspace">Inbound: Gmail (Google MX)</option>
          <option value="sending_only">Inbound: Cloudflare routing (sending-only)</option>
        </select>
        <button data-testid="gws-fix-dns-btn" onClick={a.fixDns} disabled={p.busy} className="inline-flex items-center gap-1 border border-sky-700 text-sky-300 hover:border-sky-400 hover:text-sky-200 px-3 py-1.5 rounded-sm text-xs font-medium disabled:opacity-50" title="Write any missing records into Cloudflare">
          {p.busyKind === "fix" ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />} Fix DNS
        </button>
        <button data-testid="gws-verify-btn" onClick={a.verifyNow} disabled={p.busy || p.verified} className="inline-flex items-center gap-1 border border-zinc-700 hover:border-white text-zinc-300 hover:text-white px-3 py-1.5 rounded-sm text-xs font-medium disabled:opacity-50" title="Ask Google to verify domain ownership now">
          {p.busyKind === "verify" ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Verify with Google
        </button>
        <button data-testid="gws-refresh-btn" onClick={a.refreshMatrix} disabled={p.busy} className="inline-flex items-center gap-1 text-zinc-400 hover:text-zinc-100 px-2 py-1.5 rounded-sm text-xs disabled:opacity-50" title="Refresh"><RefreshCw size={12} /></button>
      </div>
    </div>
  );
}
